import type { Timestamp } from 'firebase/firestore'
import type { EffortLevel } from './eatingPreferences'

// Client-side mirror of the engine/functions meal-plan shapes. Web does not
// depend on @parsleyai/engine, so these are kept in sync by hand. Source of
// truth: packages/engine/src/types.ts and packages/functions/src/types/mealPlanResult.ts.

export type IngredientUnit = 'g' | 'oz' | 'cup' | 'tbsp' | 'tsp' | 'piece' | ''
export type VeggieColor = 'green' | 'red' | 'orange' | 'yellow' | 'purple' | 'white'
export type DishRole = 'protein' | 'starch' | 'veggie'
export type LevelUpRole = 'hero' | 'side' | 'unity'

export interface IngredientLine {
  name: string
  quantity: number
  unit: IngredientUnit
  prepNote: string | null
  isPantryStaple: boolean
  dishRoles?: DishRole[]
  // The catalog id the line was picked from. Absent on older meals.
  ingredientId?: string
}

export interface LevelUpIngredient {
  name: string
  ingredientId: string
  quantity: number
  unit: IngredientUnit
  prepNote: string | null
  isPantryStaple: boolean
}

export interface LevelUp {
  role: LevelUpRole
  // Absent on meals made before level-ups were named.
  name?: string
  description: string
  ingredientAdditions: LevelUpIngredient[]
  instructionAddition: string
  // Positions in `cookingInstructions` of the steps that do this level-up. Absent on older meals.
  stepIndexes?: number[]
}

export interface MealComponent {
  name: string
  baseIngredient: string
  cookingMethod: string
  ingredients: IngredientLine[]
  prepTime: number
  cookTime: number
  role?: string
  veggieColor?: VeggieColor
  // One sentence on what it is and how it's cooked. Absent on older meals.
  description?: string
}

export interface Meal {
  mealName: string
  mealDescription: string | null
  proteins: MealComponent[]
  starches: MealComponent[]
  veggies: MealComponent[]
  sauce: MealComponent | null
  ingredientList: IngredientLine[]
  levelUps: LevelUp[]
  cookingInstructions: string[]
  // Absent on meals generated before the engine classified effort.
  effort?: EffortLevel
}

// Per-cook state under the meal/cook model: cooked/skipped + an optional
// per-dinner note, stored sparsely in the plan result doc's `cookState` map keyed
// by cookId. Absence of a key = ready/untouched; only cooked/skipped are ever
// stored. FE-owned — written by the owner via the plan-doc scoped rule (Sliver C),
// read by usePlan to render dinner status/notes.
export interface CookState {
  lifecycle?: 'cooked' | 'skipped'
  note?: string
  // Server time the dinner was marked cooked (stamped by markMealCooked). The
  // recipes library derives per-meal "last cooked" recency from these. Absent on
  // skipped/untouched cooks and on cooks marked before this field existed.
  cookedAt?: Timestamp
  // Server time cooking mode's Finish was last pressed for this dinner. Independent of lifecycle.
  cookingFinishedAt?: Timestamp
}

// Async image-pipeline state on the canonical meal doc, written by the generation and
// resize triggers after the meal doc lands; absent on freshly-written meals.
// Mirrors packages/functions/src/types/mealImageStatus.ts. `ready` is the sole
// "safe to cache forever" signal (see store/userData/meals/isFrozen).
export enum MealImageStatus {
  Pending = 'pending',
  Generating = 'generating',
  Ready = 'ready',
  Failed = 'failed'
}

// Rendition sizes in the meal doc's `images` map. Mirrors
// packages/functions/src/types/mealImageSize.ts.
export enum MealImageSize {
  Chip = 'chip',
  Thumb = 'thumb',
  Card = 'card',
  Hero = 'hero',
  Original = 'original'
}

// Headline plan-level status the engine derives from slot outcomes,
// stored verbatim at `result.plan.status`. Mirrors PlanStatus in
// packages/engine/src/meal-plan.ts.
export enum PlanStatus {
  SelectionPending = 'selection_pending',
  CompletedWithErrors = 'completed_with_errors',
  Failed = 'failed'
}

// Per-slot compliance violation captured by the engine. Mirrors
// ComplianceViolation in packages/engine/src/compliance.ts; `step` is typed as
// string so the web package doesn't import from the engine (same convention as
// GenerationLogEntry below).
export interface ComplianceViolation {
  step: string
  attempt: number
  bannedItem: string
  foundIn:
    | 'mealName'
    | 'mealDescription'
    | 'name'
    | 'baseIngredient'
    | 'cookingMethod'
    | 'ingredient'
    | 'prepNote'
    | 'ingredientList'
    | 'levelUpIngredient'
    | 'levelUpName'
    | 'levelUpDescription'
    | 'levelUpInstruction'
    | 'instruction'
  ingredientName?: string
}

// Whether a plan carries dates at all. Absent on the document means Scheduled — every plan
// that predates the choice behaves as it always has.
export enum PlanScheduling {
  Scheduled = 'scheduled',
  Flexible = 'flexible'
}

export enum MealPlanLifecycle {
  Draft = 'draft',
  Active = 'active',
  Complete = 'complete'
}

// Why a plan reached `complete`: every meal went terminal (cooked/skipped) so
// completion was derived, vs the user ending an active plan early with meals still
// `ready`. Persisted on the result doc alongside `lifecycle: complete` because the
// ended-early case can't be re-derived from meal state. Mirrors
// packages/functions/src/types/planCompletionReason.ts.
export enum PlanCompletionReason {
  AllCooked = 'all_cooked',
  EndedEarly = 'ended_early'
}

// Canonical meal doc at users/{uid}/meals/{mealId}.
// Full engine `Meal` payload + `mealId` + async image fields + timestamps. The
// retired per-plan meal-copy fields (planId/requestId/status/acceptance/lifecycle/
// order/note) are gone — the canonical doc carries none of
// them (cook state moved to the plan doc's cookState map).
export interface StoredMeal extends Meal {
  mealId: string
  // Servings the meal was generated for — stamped by the listener from the
  // originating request. Drives the recipes library's "Serves N".
  servings: number
  // Async image-pipeline fields, populated by the generation + resize
  // triggers AFTER the meal doc lands (absent on freshly-written docs). `images`
  // is the per-rendition URL map; `imageStatus` gates cacheability (see
  // store/userData/meals/isFrozen); `originalImagePath` is the source Storage path.
  images?: Partial<Record<MealImageSize, string>>
  imageStatus?: MealImageStatus
  originalImagePath?: string
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

// Mirrors GenerationLogEntry from @parsleyai/engine (kept in sync by hand).
// step is typed as string so the web package doesn't import from the engine.
export interface GenerationLogEntry {
  step: string
  attempt: number
  status: 'success' | 'failed' | 'fallback'
  durationMs: number
  error?: string
}

// One entry in the plan doc's slots array. `cookId` is the per-slot cook identity; `failedCookId`
// points at the failed cook doc for a failed slot (mealId null).
export interface MealPlanResultSlot {
  mealId: string | null
  cookId?: string | null
  failedCookId?: string | null
  // Which day of the plan this dinner sits on, counted from the start. Absent ⇒ it takes the
  // day after the dinner before it.
  dayOffset?: number | null
  error: string | null
  generationLog?: GenerationLogEntry[]
  complianceViolations?: ComplianceViolation[]
}

// Plan doc at users/{uid}/meal_plan_results/{requestId}; id === requestId === doc id
export interface MealPlanResult {
  id: string
  requestId: string
  planId: string
  // `status` is the engine's derived headline plan state, stored verbatim by the
  // listener. Optional — older docs lack it.
  plan: { slots: MealPlanResultSlot[]; status?: PlanStatus }
  lifecycle: MealPlanLifecycle
  createdAt: Timestamp
  // The user's requested level-ups intent, propagated onto the result doc by
  // the backend. Older docs lack the field; the store defaults it to false on read.
  levelUpsEnabled: boolean
  // User-owned plan-management metadata, written directly by the owner via the
  // client SDK (scoped Firestore rule). Absent until first set. name: human-given
  // plan name (null/absent ⇒ derive "Week of…"); notes: free text; rating: 1–5
  // stars; deletedAt: soft-delete tombstone (soft-deleted plans are filtered out
  // of this slice — physical delete is admin-only).
  name?: string | null
  notes?: string | null
  rating?: number | null
  deletedAt?: Timestamp | null
  // The day the plan's first dinner is cooked; every later dinner's date derives from
  // it. Absent on plans written before it existed — readers fall back to `createdAt`.
  startDate?: Timestamp | null
  // Absent ⇒ Scheduled. A flexible plan is an ordered list with no dates.
  scheduling?: PlanScheduling | null
  // Completion metadata, written together with the owner-driven active → complete
  // transition. Absent while draft/active. completionReason: all_cooked (every meal
  // terminal) vs ended_early (user closed it with meals still ready); completedAt
  // stamps when it happened.
  completionReason?: PlanCompletionReason | null
  completedAt?: Timestamp | null
  // FE-owned per-cook state under the meal/cook model: a sparse map keyed
  // by cookId (see CookState). Absent until the owner first marks a dinner
  // cooked/skipped or adds a note. Read by usePlan; written in Sliver C.
  cookState?: Record<string, CookState>
}

// Lifecycle of a meal-plan request doc at users/{uid}/meal_plan_requests/{requestId}.
// The backend listener (onMealPlanRequestCreated) drives the transitions:
// pending → processing → completed (result doc lands), or → failed. Mirrors
// MealPlanRequestStatus in packages/functions/src/types/mealPlanRequest.ts.
export enum MealPlanRequestStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed'
}

// Request doc at users/{uid}/meal_plan_requests/{requestId}; id === doc id (the
// requestId). planId points to the result doc the listener creates on success
// (users/{uid}/meal_plan_results/{planId}). The heavy planRequest/snapshot fields
// the backend stores here are intentionally omitted — the client only needs the
// lifecycle. Kept in sync by hand with the functions type.
/**
 * Where a plan's meals come from. Crosses the wire verbatim; the backend maps it to a favorites
 * count, so the engine still chooses WHICH favorites.
 */
export enum RecipeMix {
  Favorites = 'favorites',
  Mix = 'mix',
  AllNew = 'allNew'
}

/**
 * The week's mood — per-plan steering with no saved counterpart. Mirrors the engine's Lightness /
 * Novelty / Budget enums; web doesn't depend on @parsleyai/engine, so these are kept in sync by
 * hand, like the rest of this module. The neutral member is a real answer and IS sent when chosen.
 */
export type Lightness = 'lighter' | 'usual'
export type Novelty = 'comfort' | 'balanced' | 'adventurous'
export enum Budget {
  Lean = 'lean',
  Normal = 'normal',
  Expensive = 'expensive'
}

/** The busy-week answer as chosen. Mirrors the functions enum, kept in sync by hand. */
export enum WeekEffort {
  Slammed = 'slammed',
  Normal = 'normal',
  WideOpen = 'wideOpen'
}

export interface MealPlanRequest {
  id: string
  status: MealPlanRequestStatus
  daysRequested: number
  servings: number
  levelUpsEnabled: boolean
  // Per-plan levers, absent when the user left them alone.
  recipeMix?: RecipeMix
  weekEffort?: WeekEffort
  // The profiles that governed this plan, when they differ from the user's saved selection. Stored
  // because the snapshot records the SAVED state — without this the two would appear to contradict
  // each other, and a deliberate choice would read as an engine bug.
  activeProfileIds?: string[]
  planId?: string
  createdAt: Timestamp
  processingStartedAt?: Timestamp
  completedAt?: Timestamp
  failedAt?: Timestamp
  error?: string
}

// A request is "active" (in flight) while the engine is still working it — the
// primitive the one-plan-at-a-time UI guard builds on.
export const isActiveRequest = (request: MealPlanRequest): boolean =>
  request.status === MealPlanRequestStatus.Pending || request.status === MealPlanRequestStatus.Processing

// Number of successfully generated meals in a plan (failed slots have a null mealId).
export const mealCount = (result: MealPlanResult): number => result.plan.slots.filter((slot) => slot.mealId !== null).length

// Wall-clock cook time for one meal: its components cook in parallel, so the meal
// takes the slowest prep plus the slowest cook across all components.
export function getCookTimeMinutes(meal: Pick<Meal, 'proteins' | 'starches' | 'veggies' | 'sauce'>): number {
  const components: MealComponent[] = [...meal.proteins, ...meal.starches, ...meal.veggies, ...(meal.sauce ? [meal.sauce] : [])]
  if (components.length === 0) return 0
  const maxPrep = Math.max(...components.map((c) => c.prepTime))
  const maxCook = Math.max(...components.map((c) => c.cookTime))
  return maxPrep + maxCook
}
